import { OpenedChannel } from '@wix/communication-channel';
import type { AddSitePlugin } from './addSitePlugin';
import { GetAccessToken } from './auth';
import type { GetLanguage } from './language';
import { OpenMediaManager } from './mediaManager';
import { CloseModal, OpenModal, ProviderOpenModal } from './modal';
import { GetPageUrl, Navigate, NavigateBack } from './navigate';
import { ObserveState } from './observeState';
import { OnBeforeUnload } from './onBeforeUnload';
import { ShowToast } from './toast';
import type { RequestFeedback } from './requestFeedback';
import type { Host as SDKHost } from '@wix/sdk-types';
import type {
  WorkspaceHost,
  ItemSelectionOptions,
  AppsNamespace,
  CreditsNamespace,
} from '@wix/workspace';
import { OnLayerStateChange } from './onLayerStateChange';
import { InstallApp } from './installApp';
import { GetWidget, GetWidgetAsync } from './widget';
import { GetDashboardMenuPlugins, GetDashboardPlugins } from './slots';
import { GetSiteInfo } from './getSiteInfo';
import type { CreateStore, GetStore } from './store';

export * from './auth';
export * from './environment';
export * from './mediaManager';
export * from './modal';
export * from './navigate';
export * from './observeState';
export * from './onBeforeUnload';
export * from './serializable';
export * from './toast';
export * from './requestFeedback';
export * from './onLayerStateChange';
export * from './installApp';
export * from './getSiteInfo';
export * from './store';
export type { GetLanguage, AddSitePlugin };

export type SetPageTitle = (pageTitle: string | null) => void;

export type PostMessage = (
  message: any,
  targetOrigin: string,
  transfer: Transferable[],
) => void;

export type OpenItemSelector = (
  providerKey: string,
  options?: Omit<ItemSelectionOptions, 'providerKey'>,
) => Promise<any>;

export type SDKOptions = {
  origin?: string;
  postMessage?: PostMessage;
  autoHeightUpdate?: boolean;
  autoHeightElement?: HTMLElement;
  fullUrl?: string;
};

export type Host = LocalHost | RemoteHost;

export type LocalHost = Omit<SDKHost, 'channel'> & {
  channel: Omit<
    ProviderAPI,
    | 'updateHeight'
    | 'openItemSelector'
    | keyof AppsNamespace
    | keyof CreditsNamespace
  >;
  workspace: WorkspaceHost['workspace'];
};

export type RemoteHost = Omit<SDKHost, 'channel'> & {
  channel: OpenedChannel<
    Omit<
      ProviderAPI,
      | 'onBeforeUnload'
      | 'getSiteInfo'
      | 'getWidget'
      | 'getWidgetAsync'
      | 'getDashboardPlugins'
      | 'getDashboardMenuPlugins'
      | keyof WorkspaceHost['workspace']
      | keyof AppsNamespace
      | keyof CreditsNamespace
    >
  > &
    Pick<ProviderAPI, 'onBeforeUnload' | 'getSiteInfo'>;
  close: () => void;
  workspace: Omit<WorkspaceHost['workspace'], 'getContextIds'>;
};

export type DashboardProps = {
  host: Host;
};

export type CreateHost = (options?: SDKOptions) => RemoteHost;

export type SDK = {
  showToast: ShowToast;
  navigate: Navigate;
  navigateBack: NavigateBack;
  openModal: OpenModal;
  closeModal: CloseModal;
  getAccessToken: GetAccessToken;
  observeState: ObserveState;
  getPageUrl: GetPageUrl;
  openMediaManager: OpenMediaManager;
  addSitePlugin: AddSitePlugin;
  onBeforeUnload: OnBeforeUnload;
  setPageTitle: SetPageTitle;
  getLanguage: GetLanguage;
  requestFeedback: RequestFeedback;
  onLayerStateChange: OnLayerStateChange;
  installApp: InstallApp;
  getWidget: GetWidget;
  getWidgetAsync: GetWidgetAsync;
  getDashboardPlugins: GetDashboardPlugins;
  getDashboardMenuPlugins: GetDashboardMenuPlugins;
  getSiteInfo: GetSiteInfo;
  createStore?: CreateStore;
  getStore?: GetStore;
};

export type ProviderAPI = {
  showToast: ShowToast;
  navigate: Navigate;
  navigateBack: NavigateBack;
  updateHeight: (height: number) => void;
  observeState: ObserveState;
  closeModal: CloseModal;
  openModal: ProviderOpenModal;
  getAccessToken: GetAccessToken;
  getPageUrl: GetPageUrl;
  openMediaManager: OpenMediaManager;
  onBeforeUnload: OnBeforeUnload;
  setPageTitle: SetPageTitle;
  requestFeedback: RequestFeedback;
  onLayerStateChange: OnLayerStateChange;
  installApp: InstallApp;
  getWidget: GetWidget;
  getWidgetAsync: GetWidgetAsync;
  getDashboardPlugins: GetDashboardPlugins;
  getDashboardMenuPlugins: GetDashboardMenuPlugins;
  getSiteInfo: GetSiteInfo;
  openItemSelector?: OpenItemSelector;
  createStore?: CreateStore;
  getStore?: GetStore;
  startInstallFlow?: AppsNamespace['startInstallFlow'];
  startPurchasePlanFlow?: AppsNamespace['startPurchasePlanFlow'];
  startUpdateFlow?: AppsNamespace['startUpdateFlow'];
  openContactSupport?: AppsNamespace['openContactSupport'];
  openPaywallModal?: CreditsNamespace['openPaywallModal'];
};
